<template>
  <div class="mt-4">
    <div v-if="!isElectronicSignature">
      <div v-if="showUploadDocumentButton">
        <section-title
          class="mt-3"
          content="Datos del Documento"
          :has-line="true"
        />

        <custom-callout
          class-main="mt-3 rounded"
          :has-border="false"
          :has-toggle="false"
        >
          <div class="row py-2 mr-md-0 align-items-center">
            <div class="col-3 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">Documento:</div>
              {{ getContractDocumentName(this.contratoFirmado) }}
            </div>
            <div class="col-3 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">Archivo:</div>
              <div
                v-if="isDocumentReadOnly && hasValidFileName"
                class="color-black font-16"
              >
                {{ contractDocumentSignature.filename | empty }}
              </div>
            </div>
            <div class="col-3 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">
                Fecha de Firma de Contrato:
              </div>
              <div
                v-if="isDocumentReadOnly && hasValidSignDate"
                class="color-black font-16"
              >
                {{ contractDocumentSignature.signDate | date }}
              </div>
            </div>
            <div
              v-if="isDocumentReadOnly"
              class="col-3 mt-3 mt-md-0 d-flex justify-content-end align-items-center"
            >
              <custom-file-input
                v-if="isDocumentReadOnly && hasReplaceDocumentPermission"
                ref="fileReplacementDocumentInputRef"
                accept=".pdf"
                class="file-replacement-input__extended"
                display-style="normal"
                :just-upload="true"
                label-position="left"
                name=" "
                :on-download="onDownloadFile"
                @input="
                  (file) =>
                    onReplacementFileHandler(file, contractDocumentSignature)
                "
              />
              <action-button
                v-if="isDocumentReadOnly"
                class="btn-action-size ml-2"
                i="fas fa-file-download"
                variant="success"
                @click="() => onDownloadHandler(contractDocumentSignature)"
              />
            </div>
          </div>
          <div class="row mr-md-0 mb-2">
            <div class="col-3 mt-3 mt-md-0" />
            <div class="col-3 mt-3 mt-md-0">
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-file-input
                  v-if="!isDocumentReadOnly"
                  ref="fileUploadRef"
                  accept=".pdf"
                  add-text="Presione aqui para anejar archivo"
                  :attachment-id="document.documentId"
                  :custom-error-msg="'El documento es requerido.'"
                  display-style="normal"
                  :error="errors[0]"
                  :name="document.documentFileName"
                  :on-download="onDownloadFile"
                  required
                  :value="document.attachment"
                  @input="onChangeFile"
                />
              </validation-provider>
            </div>
            <div class="col-3 mt-3 mt-md-0">
              <validation-provider
                v-slot="{ errors }"
                name="contractSignDate"
                rules="required"
              >
                <custom-date-picker
                  v-if="!isDocumentReadOnly"
                  v-model="signDate"
                  class-container="w-100"
                  :custom-error-msg="'La fecha de firma es requerida.'"
                  :error="errors[0]"
                  format="dd/MMM./yyyy"
                  :max-date="getCurrentDate"
                  :min-date="getMinimunDateInclusive"
                  placeholder="DD/MM/YYYY"
                  @input="onChangeDate"
                />
              </validation-provider>
            </div>
            <div class="col-3 mt-3 mt-md-0" />
          </div>
        </custom-callout>
      </div>
    </div>
    <div v-if="showDocumentData" class="mt-4">
      <section-title
        class="mt-3"
        content="Datos del Documento"
        :has-line="true"
      />
      <div
        v-for="(row, index) of documentData"
        :key="`document-item-${index}`"
        class="result-table"
      >
        <custom-callout
          class-main="mt-3 rounded"
          :has-border="false"
          :has-toggle="false"
        >
          <div class="row py-2 mr-md-0 align-items-center">
            <div class="col-3 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">
                <em class="fas fa-file-alt color-orange" />
                Documento creado por:
              </div>
            </div>
            <div class="col-1 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">
                <em class="fas fa-user-circle color-orange" />
                Puesto:
              </div>
            </div>
            <div class="col-6 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">
                <em class="fas fa-file-signature color-orange" />
                Propósito del Documento:
              </div>
            </div>
            <div class="color-gray-6 font-14">
              <div class="color-gray-6 font-14">
                <em class="fas fa-calendar color-orange" />
                Creado el:
              </div>
            </div>
          </div>
          <div class="row py-2 mr-md-0 align-items-center">
            <div class="col-3 mt-3 mt-md-0">
              <div class="color-black font-16 bold">
                {{ row.createdByName }}
              </div>
            </div>
            <div class="col-1 mt-3 mt-md-0">
              <div class="color-black font-16 bold">
                {{ row.createdByRole }}
              </div>
            </div>
            <div class="col-6 mt-3 mt-md-0">
              <div class="color-black font-16 bold">
                {{ row.documentPurpose }}
              </div>
            </div>
            <div class="col-2 mt-3 mt-md-0">
              <div class="color-black font-16 bold">
                {{ row.createdDate | date('DD/MMM/YYYY') }}
              </div>
            </div>
          </div>
        </custom-callout>
      </div>
    </div>
    <div class="mt-4">
      <section-title class="mt-3" content="Participantes" :has-line="true" />
      <div
        v-for="(row, index) of signatures"
        :key="`signaturesprovider-item-${index}`"
        class="result-table"
      >
        <custom-callout
          v-if="row.roleId == null"
          class-main="mt-3 rounded"
          :has-border="false"
          :has-toggle="false"
        >
          <div class="row py-2 mr-md-0">
            <div class="col-10 mt-3 mt-md-0" style="display: inline-flex;">
              <div
                :class="`bg-${statusColor(row.participantSignatureStatusId)}`"
                style="height: 10px !important;
                width: 10px !important;
                border-radius: 20px;
                margin-top: 14px;"
              />
              <span
                class="font-16 color-black"
                style="padding-left: 10px; margin-top: 7px;"
              >
                {{ row.participantSignatureStatus || '' }}
              </span>
            </div>
            <div
              v-if="
                row.participantSignatureStatusId ==
                  signatureStatus.PendingSignature
              "
              class="col-2 mt-3 mt-md-0"
            >
              <action-button
                id="btnResend"
                class="mr-1"
                i="fas fa-arrow-circle-right fa-fw"
                text="Reenviar"
                variant="outline-success"
                @click="() => onResendHandler(row.email, row.userId)"
              />
            </div>
          </div>

          <div class="row py-2 mr-md-0 align-items-center border-top">
            <div class="col-3 col-sm col-12 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">
                {{
                  row.relationType == null || row.relationType == ''
                    ? 'Rol:'
                    : 'Parentesco:'
                }}
              </div>
              <div class="color-black font-16 bold">
                {{
                  getRelationshipName(row.relationTypeId) ||
                    row.relationType ||
                    'Proveedor'
                }}
              </div>
            </div>
            <div class="col-3 col-sm col-12 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">Nombre:</div>
              <div class="color-black font-16 bold">
                {{ row.signatoryName }}
              </div>
            </div>
            <div class="col-2 col-sm col-12 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">
                Tipo de Firma:
              </div>
              <div class="color-black font-16 bold">
                {{
                  isElectronicSignature
                    ? signatureTypeLabels.Electronic
                    : signatureTypeLabels.Handwritten
                }}
              </div>
            </div>
            <div class="col-3 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">Correo Electrónico:</div>
              <div class="color-black font-16 bold">
                {{ row.email }}
                {{ isElectronicSignature ? '/ ' + row.userName : '' }}
              </div>
            </div>
            <div class="col-2 col-sm col-12 mt-3 mt-md-0">
              <div class="color-gray-6 font-14">Fecha de Firma:</div>
              <div class="color-black font-16 bold">
                {{
                  row.participantSignatureStatusId !=
                  signatureStatus.PendingSignature
                    ? row.participantSignatureStatusDate
                    : '' | date
                }}
              </div>
            </div>
            <div class="col-1 col-sm col-12 d-flex justify-content-end">
              <action-button
                v-if="canRefreshContractorInfo(row)"
                id="refresh-contractor-info"
                class="mr-3"
                i="fas fa-redo-alt"
                style="width: 43px;"
                variant="outline-success"
                @click="handleRefreshContractorInfo(row)"
              />
            </div>
          </div>
        </custom-callout>
      </div>
    </div>
  </div>
</template>

<script>
import objectToFormData from 'object-to-formdata';
import moment from 'moment';

//libs components
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import CustomCallout from 'custom-callout';
import CustomFileInput from 'CustomFileInput';
import { ValidationProvider } from 'vee-validate';
import CustomDatePicker from 'custom-date-picker';

//services
import {
  getSignatures as _getSignatures,
  getStatusSignature as _getStatusSignature,
  getDocumentInfo as _getDocumentInfo,
  resendInvitationSigning as _resendInvitationSigning,
} from '@/services/uspqrp/signatures-contractor.api';
import { downloadDocumentByAttachmentId as _downloadDocumentByAttachmentId } from '@/services/common/document.api.js';
import { saveContractDocument as _saveContractDocument } from '@/services/uspqrp/contract.api.js';
import ParticipantSignatureStatus, {
  ParticipantSignatureStatusColor,
} from '@/utils/constants/ParticipantSignatureStatus.js';

import { updateSignatureContractorInfo as _updateSignatureContractorInfo } from '@/services/uspqrp/contract.api.js';
import { getcodeRelationshipNameByCode as _getRelationshipNameByCode } from '@/utils/constants/codeRelationship';

//utils
import roles from '@/utils/constants/roles.js';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';
import SignatureDocumentStatus from '@/utils/constants/signatureDocumentStatus.js';

//constants
import {
  contractDocumentTypes,
  contractDocumentTypesString,
} from '@/utils/constants/contractDocumentTypes.js';
import rpContractStatus from '@/utils/constants/rpContractStatus.js';
import { signatureTypeLabels } from '@/utils/constants/signatureTypes';
import { signatureStatus } from '@/utils/constants/signatureStatus';
import { getUserInformation as _getUserInformation } from '@/services/uspqrp/contract.api.js';

export default {
  name: 'ContractHistory',
  mixins: [alertMixin],
  components: {
    SectionTitle,
    CustomCallout,
    CustomFileInput,
    ValidationProvider,
    CustomDatePicker,
    ActionButton,
  },
  props: {
    contractId: {
      type: Number,
      default: 0,
      required: false,
    },
    showUploadDocumentButton: {
      type: Boolean,
      default: false,
    },
    contractDocumentSignature: {
      type: Object,
      default: () => {},
    },
    contractStatusId: {
      type: Number,
      default: () => null,
    },
    contractApprovalDate: {
      type: String,
      default: () => null,
    },
    isElectronicSignature: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: Number,
      default: 0,
      required: false,
    },
    contractDocumentIdSignature: {
      type: String,
      default: () => null,
    },
    requestServiceTypeId: {
      type: Number,
      default: 0,
      required: false,
    },
  },
  watch: {
    contractDocumentSignature: {
      handler() {
        this.getStatusParticipants();
      },
      immediate: true,
    },
  },
  data: () => ({
    signatures: [],
    documentData: [],
    signDate: null,
    contratoFirmado: contractDocumentTypes.contratoFirmado,
    document: {
      contractId: null,
      documentId: null,
      filename: null,
      attachment: null,
      documentTypeId: null,
      providerOrSpecialist: [],
    },
    signatureTypeLabels,
    signatureStatus,
    lastEmittedDocumentStatusId: null,
  }),
  async mounted() {
    this.signDate = this.contractDocumentSignature.signDate;
    this.document.documentId = this.contractDocumentSignature.documentId;
  },
  computed: {
    hasValidSignDate() {
      return (
        this.contractDocumentSignature.signDate !== undefined &&
        this.contractDocumentSignature.signDate !== null
      );
    },
    hasValidFileName() {
      return (
        this.contractDocumentSignature.filename !== undefined &&
        this.contractDocumentSignature.filename !== null
      );
    },
    isDocumentReadOnly() {
      return this.contractStatusId === rpContractStatus.Signed;
    },
    showDocumentData() {
      return (
        this.contractStatusId === rpContractStatus.InSigningProcess &&
        this.isElectronicSignature
      );
    },
    hasReplaceDocumentPermission() {
      return this.userHasPermissions(
        'rempro.contract.replacehandwrittencontractsigneddocument'
      );
    },
    getMinimunDateInclusive() {
      return moment(this.contractApprovalDate)
        .subtract(1, 'days')
        .format('YYYY-MM-DD HH:mm:ss');
    },
    getCurrentDate() {
      return moment().format('YYYY-MM-DD HH:mm:ss');
    },
    canRefreshContractorInfo() {
      return (item) => {
        const isValidForElectronic =
          this.isElectronicSignature &&
          !item.isProvider &&
          item.userId &&
          [
            rpContractStatus.New,
            rpContractStatus.Submitted,
            rpContractStatus.ApprovedContractInformation,
            rpContractStatus.DeniedContractInformation,
          ].includes(this.contractStatusId);

        const isValidForManuscrito =
          !this.isElectronicSignature &&
          !item.isProvider &&
          item.userId &&
          [rpContractStatus.InSigningProcess].includes(this.contractStatusId);

        const isTeacher =
          item.roleId &&
          item.roleId.toLowerCase() === roles.Teacher.toLowerCase();

        const isDirector =
          item.roleId &&
          item.roleId.toLowerCase() === roles.Director.toLowerCase();

        return (
          (isValidForElectronic || isValidForManuscrito) &&
          !isTeacher &&
          !isDirector
        );
      };
    },
  },
  methods: {
    statusColor(statusid) {
      const hashMap = {
        [ParticipantSignatureStatus.Signed]:
          ParticipantSignatureStatusColor[ParticipantSignatureStatus.Signed],

        [ParticipantSignatureStatus.PendingSignature]:
          ParticipantSignatureStatusColor[
            ParticipantSignatureStatus.PendingSignature
          ],
      };
      return hashMap[statusid ?? ParticipantSignatureStatus.Unset];
    },

    getContractDocumentName(contractDocumentTypeId) {
      return contractDocumentTypesString[contractDocumentTypeId];
    },
    async getSignatures(dataStatus) {
      this.signatures = (await _getSignatures(this.contractId)).data;

      this.signatures.forEach((signature) => {
        const participant = dataStatus?.participants.find(
          (x) => x.userId === signature.userId
        );
        if (participant) {
          signature.participantSignatureStatus =
            participant.participantSignatureStatus;

          signature.participantSignatureStatusId =
            participant.participantSignatureStatusId;

          signature.participantSignatureStatusDate =
            participant.participantSignatureStatusDate;
        }
      });

      if (!this.isElectronicSignature) {
        this.signatures.forEach((signature) => {
          if (
            (this.contractStatusId == rpContractStatus.InSigningProcess ||
              this.contractStatusId ==
                rpContractStatus.ApprovedContractInformation ||
              this.contractStatusId == rpContractStatus.Signed ||
              this.contractStatusId == rpContractStatus.ContractAmended) &&
            this.contractDocumentSignature?.filename
          ) {
            signature.participantSignatureStatusId =
              ParticipantSignatureStatus.Signed;

            signature.participantSignatureStatus = 'Firmado';

            signature.participantSignatureStatusDate = this.contractDocumentSignature.signDate;
          }
        });
      }

      const signaturesWithUserNames = await Promise.all(
        this.signatures.map(async (item) => {
          const rs = await _getUserInformation(item.userId);
          return {
            ...item,
            userName: rs.data.userName,
          };
        })
      );

      this.signatures = signaturesWithUserNames;
    },
    onDownloadFile(id) {
      if (id) {
        return new Promise((resolve, reject) => {
          _downloadDocumentByAttachmentId(id)
            .then(() => resolve())
            .catch((err) => reject(err));
        });
      }
    },
    async onDownloadHandler(item) {
      if (item) {
        _downloadDocumentByAttachmentId(item.documentId).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });
      }
    },
    onChangeFile(file) {
      if (file) {
        this.contractDocumentSignature.documentTypeId =
          contractDocumentTypes.contratoFirmado;
        this.contractDocumentSignature.filename = file?.name;
        this.contractDocumentSignature.attachment = file;
        this.contractDocumentSignature.documentTypeName = this.getContractDocumentName(
          this.contratoFirmado
        );
      } else {
        this.document.filename = null;
        this.document.attachment = null;
      }
    },
    onChangeDate(value) {
      this.contractDocumentSignature.signDate = value;
    },
    async saveContractDocument(
      contractDocumentTypeId,
      file,
      contractDocumentId
    ) {
      const payload = objectToFormData(
        {
          contractDocumentId,
          contractId: this.contractId,
          academicYearId: this.currentAcademicYearId,
          contractDocumentTypeId,
          file,
        },
        { indices: true, nullsAsUndefineds: true }
      );
      await _saveContractDocument(payload);
    },
    async onReplacementFileHandler(file, item) {
      if (file && item.contractDocumentId) {
        await this.saveContractDocument(
          item.documentTypeId,
          file,
          item.contractDocumentId
        );
        this.$emit('on-refresh-document');
      }
    },
    async getStatusParticipants() {
      if (this.contractDocumentIdSignature) {
        const { data: dataStatus } = await _getStatusSignature(
          this.contractDocumentIdSignature
        );
        const { data: docInfo } = await _getDocumentInfo(
          this.contractDocumentIdSignature
        );
        this.documentData = [docInfo];
        const currentStatusId = docInfo?.statusInformation?.documentStatusId;
        if (
          currentStatusId === SignatureDocumentStatus.Signed &&
          this.lastEmittedDocumentStatusId !== currentStatusId
        ) {
          this.lastEmittedDocumentStatusId = currentStatusId;
          this.$emit('document-signed');
        }
        await this.getSignatures(dataStatus);
      } else {
        await this.getSignatures(null);
      }
    },
    async onResendHandler(email, userId) {
      const { data } = await _resendInvitationSigning({
        email,
        documentId: this.contractDocumentIdSignature,
        userId,
      });

      if (data) {
        this.ShowToast(
          'Alerta',
          'Se ha reenviado el mensaje satisfactoriamente.',
          'success'
        );
      } else {
        this.ShowToast(
          'Alerta',
          'Ocurrió un error, intente nuevamente.',
          'error'
        );
      }
    },
    async handleRefreshContractorInfo(row) {
      if (this.contractId && row.userId) {
        const response = await _updateSignatureContractorInfo(
          this.contractId,
          row.userId
        ).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });
        if (response?.data) {
          await this.getStatusParticipants();
          this.ShowToast(
            '',
            'La información del participante ha sido actualizada exitosamente.',
            'success'
          );
        }
      }
    },
    getRelationshipName(code) {
      return _getRelationshipNameByCode(code);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';
::v-deep .file-input-container {
  em {
    padding-right: 5px !important;
  }
}
.btn-action-size {
  width: 35px;
  height: 35px;
}
.border-top {
  border: 1px, 0px, 0px, 0px;
  border-color: #c3c3c3;
}
.file-replacement-input__extended ::v-deep {
  .file-input-container {
    width: 35px;
    height: 35px !important;
    border-color: map-get($colors, 'green');
    em {
      font-size: 18px;
      margin-left: -2px;
    }
    em.fas.fa-file-plus.pr-4.text-green:before {
      content: '\f0e2';
    }
    .file-select {
      span {
        display: none;
      }
    }
  }
}
</style>
